import { useLazyQuery, useMutation } from "@apollo/client";
import { useDispatch, useSelector } from "react-redux";
import { setViewData } from "../../features/recentlyView/RecentlyViewSlice";
import RECENTY_VIEWED_DEFAULT_OPERATIONS from "../RecentlyViewedPage/RecentyViewed.gql";
import { useCallback, useEffect, useMemo, useState } from "react";
import { AlertCircle, Info } from "react-feather";
import { BsHandbag } from "react-icons/bs";
import { GET_3D_MODEL } from "../InspirationZone/HomeHotSpot.gql.js";
import classes from "../../containers/ProductFullDetail/productFullDetail.module.css";
import { useToasts } from "react-toast-notifications";
import PINCODE_DEFAULT_OPERATIONS from "../../Pregrine/PinCode/PinCode.gql";
import DEFAULT_OPERATIONS from "./ProductfullDetail.gql.js";
import Modal from "react-bootstrap/Modal";
import { findMatchingVariant } from "../../components/findMatchingVariant/findMatchingVariant";
import { IoCartOutline } from "react-icons/io5";
import { useNavigate } from "react-router-dom";
import { logGenerator } from "../../utility/logGenerator.js";
import { saveShowEnquirePrdouctName } from "../../features/HomePage/HomePageSlice.js";
import { useCreateCart } from "../CartPage/useCreateCart.js";

const EMAIL_REGEX = /^[\w-]+(\.[\w-]+)*@[\w-]+(\.[\w-]{2,10})+$/;

const SUPPORTED_PRODUCT_TYPES = [
  "SimpleProduct",
  "ConfigurableProduct",
  "BundleProduct",
];

const AlertCircleIcon = <AlertCircle width={18} />;

const INITIAL_OPTION_CODES = new Map();
const INITIAL_OPTION_SELECTIONS = new Map();
const OUT_OF_STOCK_CODE = "OUT_OF_STOCK";

const deriveOptionCodesFromProduct = (product) => {
  if (!(product.__typename === "ConfigurableProduct")) {
    return INITIAL_OPTION_CODES;
  }

  const initialOptionCodes = new Map();
  for (const { attribute_id, attribute_code } of product.configurable_options) {
    initialOptionCodes.set(attribute_id, attribute_code);
  }

  return initialOptionCodes;
};

const deriveOptionSelectionsFromProduct = (product) => {
  if (!(product.__typename === "ConfigurableProduct")) {
    return INITIAL_OPTION_SELECTIONS;
  }

  const { configurable_options, base_variants } = product;
  if (base_variants && configurable_options) {
    const baseVariantAttributes = new Map();
    base_variants[0]?.attributes.forEach((attr) => {
      baseVariantAttributes.set(attr.code, attr.value_index);
    });

    const optionMap = new Map();
    configurable_options.forEach((option) => {
      const valueIndex = baseVariantAttributes.get(option.attribute_code);
      if (valueIndex !== undefined) {
        optionMap.set(option.attribute_id, valueIndex);
      } else {
        optionMap.set(option.attribute_id, option.values[0]?.value_index);
      }
    });
    return optionMap;
  } else if (configurable_options) {
    const optionMap = new Map();
    configurable_options.forEach((item) => {
      optionMap.set(item.attribute_id, item.values[0]?.value_index);
    });
    return optionMap;
  }
};

const getIsMissingOptions = (product, optionSelections) => {
  if (!(product.__typename === "ConfigurableProduct")) {
    return false;
  }

  const { configurable_options } = product;
  const numProductOptions = configurable_options.length;
  const numProductSelections = Array.from(optionSelections.values()).filter(
    (value) => !!value
  ).length;

  return numProductSelections < numProductOptions;
};

const getIsOutOfStock = (
  product,
  optionCodes,
  optionSelections,
  selectedBundle
) => {
  if (product.__typename === "BundleProduct") {
    const { items } = product;
    if (selectedBundle) {
      const bundleOptions = items.find(
        (item) => item.option_id === selectedBundle
      );
      for (let i = 0; i < bundleOptions.options.length; i++) {
        if (
          bundleOptions.options[i].product.stock_status === OUT_OF_STOCK_CODE
        ) {
          return true;
        }
      }
    }
    return false;
  } else {
    const { stock_status, variants } = product;
    const isConfigurable = product.__typename === "ConfigurableProduct";
    const optionsSelected =
      Array.from(optionSelections.values()).filter((value) => !!value)
        .length === product?.configurable_options?.length;

    if (isConfigurable && optionsSelected) {
      const item = findMatchingVariant({
        optionCodes,
        optionSelections,
        variants,
      });

      return (item && item.product.stock_status === OUT_OF_STOCK_CODE) || !item;
    }
    return stock_status === OUT_OF_STOCK_CODE;
  }
};

const getMediaGalleryEntries = (
  product,
  optionCodes,
  optionSelections,
  selectedBundle
) => {
  let value = [];

  const { media_gallery, variants, product_video } = product;
  const isConfigurable = product.__typename === "ConfigurableProduct";
  const isBundle = product.__typename === "BundleProduct";

  const optionsSelected =
    Array.from(optionSelections.values()).filter((value) => !!value).length ===
    product?.configurable_options?.length;

  if (!isConfigurable || !optionsSelected) {
    if (isBundle) {
      if (selectedBundle) {
        const bundleOption = product.items.find(
          (item) => item.option_id === selectedBundle
        );
        if (bundleOption) {
          if (product.simplebundle) {
            let galleryItems = [];
            if (product.product_video?.url) {
              galleryItems = [...product.media_gallery, product.product_video];
            } else {
              galleryItems = [...product.media_gallery];
              return galleryItems;
            }
            value = galleryItems;
          } else {
            const mediaGallery = bundleOption.options.map((item) => {
              let galleryItems = [];
              if (item.product.product_video.url) {
                galleryItems = [
                  ...item.product.media_gallery,
                  item.product.product_video,
                ];
              } else {
                galleryItems = [...item.product.media_gallery];
              }
              return galleryItems;
            });

            value = mediaGallery.flat(1);
          }
        }
      }
    } else {
      value =
        media_gallery.length > 0
          ? product_video.url
            ? [...media_gallery, product_video]
            : media_gallery
          : product_video.url
          ? [product_video]
          : [];
    }
  } else {
    const item = findMatchingVariant({
      optionCodes,
      optionSelections,
      variants,
    });

    value = item
      ? item?.product?.media_gallery?.length
        ? item?.product?.product_video?.url
          ? [...item.product.media_gallery, item.product.product_video]
          : item.product.media_gallery
        : product_video.url
        ? [product_video]
        : []
      : media_gallery.length > 0
      ? product_video.url
        ? [...media_gallery, product_video]
        : media_gallery
      : product_video.url
      ? [product_video]
      : [];
  }

  return value;
};

const getConfigPrice = (
  product,
  optionCodes,
  optionSelections,
  selectedBundle
) => {
  let value = {};

  const { variants } = product;
  const isConfigurable = product.__typename === "ConfigurableProduct";

  const optionsSelected =
    Array.from(optionSelections.values()).filter((value) => !!value).length ===
    product?.configurable_options?.length;

  if (!isConfigurable || !optionsSelected) {
    if (product["__typename"].toLowerCase() === "bundleproduct") {
      if (selectedBundle) {
        const bundleOptions = product.items.find(
          (item) => item.option_id === selectedBundle
        );
        const totalPrice = bundleOptions.options.reduce((acc, curr) => {
          return (
            acc +
            curr.product.price_range.maximum_price.regular_price.value *
              curr.quantity
          );
        }, 0);

        const afterDiscount = bundleOptions.options.reduce((acc, curr) => {
          return (
            acc +
            curr.product.price_range.maximum_price.final_price.value *
              curr.quantity
          );
        }, 0);

        value = {
          regularPrice: { value: totalPrice },
          discountPrice: { value: afterDiscount },
        };
      }
    } else {
      const regularPrice = product.price_range.minimum_price.regular_price;
      const discountPrice =
        product.price_range.minimum_price.discount &&
        !!product.price_range.minimum_price.discount.amount_off &&
        product.price_range.minimum_price.final_price;
      value = {
        regularPrice,
        discountPrice,
      };
    }
  } else {
    const item = findMatchingVariant({
      optionCodes,
      optionSelections,
      variants,
    });
    if (item) {
      const { product } = item;

      const regularPrice = product.price_range.minimum_price.regular_price;
      const discountPrice =
        product.price_range.minimum_price.discount &&
        !!product.price_range.minimum_price.discount.amount_off &&
        product.price_range.minimum_price.final_price;

      value = {
        regularPrice,
        discountPrice,
      };
    }
  }

  return value;
};

const getConfigProductVariantId = (product, optionCodes, optionSelections) => {
  const { variants } = product;
  const isConfigurable = product.__typename === "ConfigurableProduct";
  if (isConfigurable) {
    const item = findMatchingVariant({
      optionCodes,
      optionSelections,
      variants,
    });

    if (item && item.product) {
      return item.product.id;
    } else {
      return null;
    }
  } else {
    return product.id;
  }
};

const getProductDetails = (
  product,
  optionCodes,
  optionSelections,
  selectedBundle
) => {
  let result = {
    specifications: [],
    careInstructions: null,
    fitting_instructions: null,
    description: null,
  };

  const { variants } = product;
  const isConfigurable = product.__typename === "ConfigurableProduct";
  const isBundle = product.__typename === "BundleProduct";
  const optionsSelected =
    Array.from(optionSelections.values()).filter((value) => !!value).length ===
    product?.configurable_options?.length;

  if (!isConfigurable || !optionsSelected) {
    if (isBundle && selectedBundle) {
      if (product.simplebundle) {
        result = {
          specifications: [...product.specifications],
          careInstructions: product.care_instructions,
          fitting_instructions: product.fitting_instructions,
          description: product.description,
          descriptionText: product.description_text,
          short_description: product.short_description,
          product_highlights: product.product_highlights,
          only_x_left_in_stock: product.only_x_left_in_stock,
          enable_site_visit: product.enable_site_visit,
          enable_appliance_site_visit: product.enable_appliance_site_visit,
          flag_text: product.flag_text,
          sku: product?.sku,
          id: product?.id,
          unit_text: product?.unit_text,
          warranty: product?.warranty_text,
          warranty_appliances: product?.warranty_appliances,
          uid: product?.uid,
          name: product?.name,
          stock_status: product?.stock_status,
          url_key: product?.url_key,
          influencer_videos: product?.product_tab_videos,
          __typename: product?.__typename,
          price_range: product?.price_range,
        };
        return result;
      } else {
        const bundleOption = product.items.find(
          (item) => item.option_id === selectedBundle
        );

        const productInOption = bundleOption.options[0].product;

        // console.log("productInOption", productInOption);

        result = {
          specifications: [...productInOption.specifications],
          careInstructions: productInOption.care_instructions,
          fitting_instructions: productInOption.fitting_instructions,
          description: productInOption.description,
          descriptionText: productInOption.description_text,
          short_description: productInOption.short_description,
          product_highlights: productInOption.product_highlights,
          only_x_left_in_stock: productInOption.only_x_left_in_stock,
          enable_site_visit: productInOption.enable_site_visit,
          enable_appliance_site_visit:
            productInOption.enable_appliance_site_visit,
          sku: product.sku,
          id: productInOption?.id,
          unit_text: product?.unit_text,
          flag_text: "",
          warranty: productInOption?.warranty_text,
          warranty_appliances: product?.warranty_appliances,
          uid: productInOption?.uid,
          name: product?.name,
          stock_status: productInOption?.stock_status,
          url_key: productInOption?.url_key,
          influencer_videos: product?.product_tab_videos,
          price_range: product?.price_range,
          __typename: productInOption?.__typename,
        };
        return result;
      }
    } else {
      result.specifications = [...product.specifications];
      result.careInstructions = product.care_instructions
        ? product.care_instructions
        : null;
      result.fitting_instructions = product.fitting_instructions
        ? product.fitting_instructions
        : null;
      result.description = product.description;
      result.price_range = product?.price_range;
      result.descriptionText = product.description_text;
      result.unit_text = product.unit_text;
      result.only_x_left_in_stock = product.only_x_left_in_stock;
      result.enable_site_visit = product.enable_site_visit;
      result.enable_appliance_site_visit = product.enable_appliance_site_visit;
      result.short_description = product.short_description;
      result.product_highlights = product.product_highlights;
      result.warranty = product?.warranty_text;
      result.warranty_appliances = product?.warranty_appliances;
      result.sku = product?.sku;
      result.id = product?.id;
      result.flag_text = product.flag_text;
      result.uid = product?.uid;
      result.name = product?.name;
      result.stock_status = product?.stock_status;
      result.url_key = product?.url_key;
      result.influencer_videos = product?.product_tab_videos;
      result.__typename = product?.__typename;
    }
  } else {
    const item = findMatchingVariant({
      optionCodes,
      optionSelections,
      variants,
    });
    if (item) {
      result.specifications = [...item.product.specifications];
      result.careInstructions = item.product.care_instructions
        ? item.product.care_instructions
        : null;
      result.fitting_instructions = item.product.fitting_instructions
        ? item.product.fitting_instructions
        : null;
      result.description = item.product.description;
      result.descriptionText = item.product.description_text;
      result.price_range = item.product.price_range;
      result.unit_text = item.product.unit_text;
      result.short_description = item.product.short_description;
      result.product_highlights = item.product.product_highlights;
      result.only_x_left_in_stock = item.product.only_x_left_in_stock;
      result.enable_site_visit = item.product.enable_site_visit;
      result.enable_appliance_site_visit =
        item.product.enable_appliance_site_visit;
      result.sku = item?.product?.sku;
      result.id = item?.product?.id;
      result.warranty = item?.product?.warranty_text;
      result.flag_text = item?.product.flag_text;
      result.warranty_appliances = item?.product?.warranty_appliances;
      result.uid = item?.product?.uid;
      result.name = item?.product?.name;
      result.stock_status = item?.product?.stock_status;
      result.url_key = item?.product?.url_key;
      result.influencer_videos = item?.product?.product_tab_videos?.length
        ? item?.product?.product_tab_videos
        : product.product_tab_videos;
      result.__typename = item?.product?.__typename;
    } else {
      result.specifications = [...product.specifications];
      result.careInstructions = product.care_instructions
        ? product.care_instructions
        : null;
      result.fitting_instructions = product.fitting_instructions
        ? product.fitting_instructions
        : null;
      result.description = product.description;
      result.descriptionText = product.description_text;
      result.unit_text = item.product.unit_text;
      result.price_range = item.product.price_range;
      result.short_description = product.short_description;
      result.product_highlights = product.product_highlights;
      result.only_x_left_in_stock = product.only_x_left_in_stock;
      result.enable_site_visit = product.enable_site_visit;
      result.enable_appliance_site_visit = product.enable_appliance_site_visit;
      result.sku = item?.product?.sku;
      result.id = item?.product?.id;
      result.warranty = item?.product?.warranty_text;
      result.flag_text = product.flag_text;
      result.warranty_appliances = item?.product?.warranty_appliances;
      result.uid = item?.product?.uid;
      result.name = product?.name;
      result.price_range = item?.product?.price_range;
      result.url_key = item?.product?.url_key;
      result.influencer_videos = item?.product?.product_tab_videos?.length
        ? item?.product?.product_tab_videos
        : product?.product_tab_videos;
      result.__typename = item?.product?.__typename;
    }
  }
  return result;
};

function getConfigurableOptionSelections(
  configurableOptions,
  optionsSelections
) {
  // Result array to store the selected options
  const selectedOptions = [];

  // Iterate through each entry in the optionsSelections map
  optionsSelections.forEach((valueIndex, attributeId) => {
    // Find the corresponding configurable option object by attributeId
    const option = configurableOptions.find(
      (option) => option.attribute_id === attributeId
    );

    if (option) {
      // Find the value within this option where the value_index matches the selected valueIndex
      const selectedValue = option.values.find(
        (value) => value.value_index === valueIndex
      );

      if (selectedValue) {
        // Add the selected option label and value to the selectedOptions array
        selectedOptions.push({
          attribute_code: option.attribute_code,
          label: option.label,
          selected_value_label: selectedValue.label,
          selected_value_index: selectedValue.value_index,
        });
      }
    }
  });

  return selectedOptions;
}

export const useProductFullDetail = (props) => {
  const { product, setProductSchemaData } = props;
  const [openPinCodeModal, setOpenPinCodeModal] = useState(false);
  const [isScrolledDown, setIsScrolledDown] = useState(false);
  const [zoom, setProductZoom] = useState(false);
  const [isnotified, setIsNotified] = useState(false);
  const [NotifyEmail, setNotifyEmail] = useState("");
  const [NotifyEmailError, setNotifyEmailError] = useState("");
  const [buttonIsOpen, setButtonIsOpen] = useState(false);
  const [showReviewModal, setShowReviewModal] = useState(false);
  const [showThreeDModal, setShowThreeDModal] = useState(false);
  const [buyNow, setBuyNow] = useState(false);
  const { isSignedIn } = useSelector((state) => state.loginSlice);
  const recentlyViewedData = useSelector((state) => state.recentlyViewed);
  const [pincodeDataFromModal, setPincodeDataFromModal] = useState(null);
  const [categoryNames, setCategoryNames] = useState([]);
  const [alreadyViewed, setAlreadyViewed] = useState([]);
  const [influencerModalData, setInfluencerModalData] = useState({
    show: false,
    data: null,
  });
  const [userData, setUserData] = useState(
    JSON.parse(localStorage.getItem("userData"))
  );

  const handleScroll = () => {
    const scrolledDown = window.scrollY > 0;
    setIsScrolledDown(scrolledDown);
  };

  useEffect(() => {
    window.addEventListener("scroll", handleScroll);

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);
  const { cartId } = useSelector((state) => state.cartSlice);
  const [selectedBundle, setSelectedBundle] = useState(null);

  const [quantity, setQuantity] = useState(1);
  const { CreateEmptyCart, CreateEmptyCartAfterSignIn } = useCreateCart();

  const { addRecentViewedProduct } = RECENTY_VIEWED_DEFAULT_OPERATIONS;
  const { checkPinCodeQuery } = PINCODE_DEFAULT_OPERATIONS;
  const {
    notifyProductQuery,
    addProductToCartMutation,
    getConfigurationsQuery,
    getVirtualProductQuery,
  } = DEFAULT_OPERATIONS;

  const productType = product.__typename;
  const isSupportedProductType = SUPPORTED_PRODUCT_TYPES.includes(
    product.__typename
  );

  const dispatch = useDispatch();
  const { addToast } = useToasts();
  const navigate = useNavigate();

  const [addrecentViewedProduct] = useMutation(addRecentViewedProduct, {
    variables: {
      products: product.id,
    },
    skip: !isSignedIn,
  });

  const [
    get3dModel,
    { data: modelData, loading: modelLoading, error: modelError },
  ] = useLazyQuery(GET_3D_MODEL);

  const [checkPincode, { data }] = useLazyQuery(checkPinCodeQuery, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      if (data?.pin_code?.status === "1") {
        handleAddToCart(quantity);
      } else {
        addToast(
          "Delivery not available to this location, Please check for other",
          {
            appearance: "warning",
            autoDismiss: true,
            icon: AlertCircleIcon,
            style: {
              backgroundColor: "#745600",
              color: "#fff",
            },
          }
        );
      }
    },
  });

  const [
    notifyProduct,
    { data: notifiyData, error: notifyError, loading: notifyLoading },
  ] = useMutation(notifyProductQuery, {
    onCompleted: (data) => {
      if (
        data &&
        data.ProductAlertNotifyInStock &&
        data.ProductAlertNotifyInStock.message
      ) {
        setIsNotified(true);

        addToast(data.ProductAlertNotifyInStock.message, {
          appearance: "warning",
          autoDismiss: true,
          icon: AlertCircleIcon,
          style: {
            backgroundColor: "#745600",
            color: "#fff",
          },
        });
      } else {
        return;
      }
    },
  });

  const [
    getVirtualProduct,
    {
      data: vritualProductData,
      error: vritualProductError,
      loading: vritualProductLoading,
    },
  ] = useLazyQuery(getVirtualProductQuery, {
    fetchPolicy: "no-cache",
  });
  const [shouldAddToCart, setShouldAddToCart] = useState(false);
  // TODO: AddProductToCart in DetailsPage
  const [
    addProductToCart,
    { error: errorAddingProductToCart, loading: isAddProductLoading },
  ] = useMutation(addProductToCartMutation, {
    onCompleted: (data) => {
      if (buyNow === true) {
        if (data?.addProductsToCart?.user_errors?.length >= 1) {
          addToast(data?.addProductsToCart?.user_errors[0].message, {
            appearance: "error",
            autoDismiss: true,
            style: {
              backgroundColor: "#DB1F00",
              color: "#fff",
            },
          });
        } else {
          navigate(`/checkout`, { replace: true });
        }
      } else {
        if (data?.addProductsToCart?.user_errors?.length >= 1) {
          addToast(data?.addProductsToCart?.user_errors[0].message, {
            appearance: "error",
            autoDismiss: true,
            style: {
              backgroundColor: "#DB1F00",
              color: "#fff",
            },
          });
        } else {
          addToast("Item successfully Added to Cart", {
            appearance: "success",
            autoDismiss: true,
            style: {
              backgroundColor: "#027B51" /* Custom background color */,
              color: "#fff" /* Custom text color */,
            },
          });
        }
      }
    },
    onError: async (err) => {
      if ("debugMessage" in err?.graphQLErrors[0]) {
        addToast(err.graphQLErrors[0]?.debugMessage, {
          appearance: "warning",
          autoDismiss: true,
          style: {
            backgroundColor: "#745600",
            color: "#fff",
          },
        });
      } else {
        console.log("err.message", err.message);
        if (
          err.message.includes("Could not find a cart with ID") ||
          err.message.includes("Cart isn't active") ||
          err.message.includes("The cart isn't active.")
        ) {
          if (isSignedIn) {
            console.log("inside signed in", err.message);
            await CreateEmptyCartAfterSignIn();
          } else {
            console.log("inside not signed in", err.message);
            await CreateEmptyCart();
          }
          console.log("after create new cart");
          setShouldAddToCart(true);
        } else if (err.message !== `Required parameter "cartId" is missing`) {
          addToast(err.message, {
            appearance: "error",
            autoDismiss: true,
            style: {
              backgroundColor: "#DB1F00",
              color: "#fff",
            },
          });
        }
      }
    },
  });

  useEffect(() => {
    if (shouldAddToCart && cartId) {
      handleAddToCart(quantity);
      setShouldAddToCart(false); // Reset
    }
  }, [cartId, shouldAddToCart]);

  const derivedOptionSelections = useMemo(
    () => deriveOptionSelectionsFromProduct(product),
    [product]
  );

  const [optionSelections, setOptionSelections] = useState(
    derivedOptionSelections
  );

  const derivedOptionCodes = useMemo(
    () => deriveOptionCodesFromProduct(product),
    [product]
  );

  const [combinations, setCombinations] = useState([]);
  const [currentCombinationIndex, setCurrentCombinationIndex] = useState(0);

  const [availableOptions, setAvailableOptions] = useState();
  const filterAttributes = (
    configurableOptions,
    currentCombinationIndex,
    totalAvailableOptions
  ) => {
    const combination = combinations[currentCombinationIndex];
    const nonNullAndNonUndefinedValues = Array.from(
      combination.entries()
    ).filter(([key, value]) => value !== null && value !== undefined);

    let filtered = configurableOptions;
    let tempAvailable = totalAvailableOptions.map((option) => ({ ...option })); // Create a shallow copy to avoid mutating original

    if (nonNullAndNonUndefinedValues.length > 1) {
      filtered = configurableOptions.filter((item) => {
        const value = combination.get(item.attribute_id);
        return !value;
      });
    } else {
      const [key, value] = nonNullAndNonUndefinedValues[0];
      const primaryOption = configurableOptions.filter(
        (option) => option.attribute_id === key
      );

      if (primaryOption.length > 0) {
        const primaryValueUids = primaryOption[0].values.map(
          (value) => value.uid
        );

        const attributeToUpdate = tempAvailable.find(
          (item) => item.attribute_code === primaryOption[0].attribute_code
        );

        // If the attribute is found, update its option_value_uids
        if (attributeToUpdate) {
          attributeToUpdate.option_value_uids = primaryValueUids;
        }
      } else {
        console.error(`No primary option found for attribute_id: ${key}`);
      }
    }

    const filteredData = tempAvailable.filter((option) =>
      filtered.some((attr) => attr.attribute_code === option.attribute_code)
    );
    setAvailableOptions((prevState) =>
      prevState
        ? prevState?.map((attr) => {
            const matchingAttr = filteredData.find(
              (newAttr) => newAttr.attribute_code === attr.attribute_code
            );
            if (matchingAttr) {
              return {
                ...attr,
                option_value_uids: matchingAttr.option_value_uids,
              };
            }
            return attr;
          })
        : filteredData
    );
  };

  const [
    getAvailableConfigurationsRequest,
    { data: availableConfigurationsResponse },
  ] = useLazyQuery(getConfigurationsQuery, {
    fetchPolicy: "network-only",
    context: { fetchOptions: { method: "GET" } },
    onCompleted: (data) => {
      const totalAvailableOptions =
        data.products.items[0].configurable_product_options_selection
          .options_available_for_selection;
      if (currentCombinationIndex <= combinations.length - 1) {
        setCurrentCombinationIndex((prevIndex) => {
          filterAttributes(
            product.configurable_options,
            prevIndex,
            totalAvailableOptions
          );
          return prevIndex + 1;
        });
      }
    },
  });

  const getUIDs = (configOptions, valueIndexMap) => {
    let result = [];

    configOptions.forEach((option) => {
      const attributeId = option.attribute_id;
      const valueIndex = valueIndexMap.get(attributeId);

      if (valueIndex !== null) {
        option.values.forEach((value) => {
          if (value.value_index === valueIndex) {
            result.push(value.uid);
          }
        });
      }
    });

    return result;
  };

  useEffect(() => {
    // Function to generate all combinations of optionSelections
    const generateCombinations = (map) => {
      const keys = Array.from(map.keys());
      const values = Array.from(map.values());

      const combinations = [];

      for (let i = 0; i < keys.length; i++) {
        const newCombination = new Map(map);
        for (let j = i + 1; j < keys.length; j++) {
          newCombination.set(keys[j], undefined);
        }
        combinations.push(newCombination);
      }

      return combinations;
    };

    const hasAtLeastOneValue = Array.from(optionSelections.values()).some(
      (value) => value !== null && value !== undefined
    );

    if (hasAtLeastOneValue) {
      const generatedCombinations = generateCombinations(optionSelections);
      setCombinations(generatedCombinations);
      setCurrentCombinationIndex(0);

      const uids = getUIDs(
        product?.configurable_options,
        generatedCombinations[0]
      );
      getAvailableConfigurationsRequest({
        variables: {
          sku: product.sku,
          optionValueUids: uids,
        },
      });
    } else {
      const formattedOptions = product?.configurable_options?.map((option) => {
        return {
          attribute_code: option.attribute_code,
          option_value_uids: option.values.map((value) => value.uid),
        };
      });

      setAvailableOptions(formattedOptions);
    }
  }, [optionSelections]);

  useEffect(() => {
    if (
      combinations.length > 0 &&
      currentCombinationIndex > 0 &&
      currentCombinationIndex < combinations.length
    ) {
      const combination = combinations[currentCombinationIndex];
      const uids = getUIDs(product?.configurable_options, combination);
      getAvailableConfigurationsRequest({
        variables: {
          sku: product.sku,
          optionValueUids: uids,
        },
      });
    }
  }, [combinations, currentCombinationIndex]);

  const [optionCodes] = useState(derivedOptionCodes);

  const isMissingOptions = useMemo(
    () => getIsMissingOptions(product, optionSelections),
    [product, optionSelections]
  );

  const isOutOfStock = useMemo(
    () =>
      getIsOutOfStock(product, optionCodes, optionSelections, selectedBundle),
    [product, optionCodes, optionSelections, selectedBundle]
  );

  const mediaGalleryEntries = useMemo(
    () =>
      getMediaGalleryEntries(
        product,
        optionCodes,
        optionSelections,
        selectedBundle
      ),
    [product, optionCodes, optionSelections, selectedBundle]
  );

  const configProductVariantId = useMemo(
    () => getConfigProductVariantId(product, optionCodes, optionSelections),
    [product, optionCodes, optionSelections]
  );

  const productDetails = useMemo(
    () =>
      getProductDetails(product, optionCodes, optionSelections, selectedBundle),
    [product, optionCodes, optionSelections, selectedBundle]
  );

  useEffect(() => {
    if (productDetails) setProductSchemaData(productDetails);
  }, [productDetails]);

  useEffect(() => {
    if (productDetails.enable_site_visit === 1) {
      getVirtualProduct({
        variables: {
          eq: "booksitevisit",
        },
      });
    } else if (productDetails.enable_appliance_site_visit === 1) {
      getVirtualProduct({
        variables: {
          eq: "booksitevisitappliance",
        },
      });
    }
  }, [productDetails]);

  const attributeIdToValuesMap = useMemo(() => {
    const map = new Map();
    const options = product.configurable_options || [];
    for (const { attribute_id, values } of options) {
      map.set(attribute_id, values);
    }
    return map;
  }, [product.configurable_options]);

  const selectedOptionsArray = useMemo(() => {
    const selectedOptions = [];

    optionSelections.forEach((value, key) => {
      const values = attributeIdToValuesMap.get(key);

      const selectedValue = values.find((item) => item.value_index === value);

      if (selectedValue) {
        selectedOptions.push(selectedValue.uid);
      }
    });
    return selectedOptions;
  }, [attributeIdToValuesMap, optionSelections]);

  const selectedVariantsArray = useMemo(() => {
    const { configurable_options } = product;
    const selectionObjArray = getConfigurableOptionSelections(
      configurable_options,
      optionSelections
    );

    const variants = selectionObjArray.map((item) => {
      return {
        label: item.label,
        value: item.selected_value_label,
      };
    });

    return variants;
  }, [optionSelections]);

  const handleAddToCart = async (quantity) => {
    console.log("cartId", cartId);

    let variables;
    let categoryCount = 1;
    const categoryItems = categoryNames.reduce((acc, item) => {
      if (item && item?.toLowerCase() !== "category" && item !== "product") {
        acc[`item_category${categoryCount}`] = item;
        categoryCount++;
      }
      return acc;
    }, {});
    if (
      productDetails.enable_site_visit === 1 ||
      productDetails.enable_appliance_site_visit === 1
    ) {
      variables = {
        cartId,
        product: {
          sku: vritualProductData?.products?.items[0]?.sku,
          quantity: quantity,
          entered_options: [
            {
              uid: vritualProductData?.products?.items[0]?.options[0]?.uid,
              value: productDetails.name,
            },
          ],
        },
      };
    } else {
      variables = {
        cartId,
        product: {
          sku: product.sku,
          quantity,
          entered_options: [
            {
              uid: product.uid,
              value: product.name,
            },
          ],
        },
      };
    }

    if (selectedOptionsArray.length) {
      variables.product.selected_options = selectedOptionsArray;
    }

    try {
      const item = {
        item_id: productDetails.sku,
        item_name: productDetails.name,
        item_brand: "Hettich",
        index: 0,
        affiliation: "Hettich India Online",
        quantity: quantity,
        price: discountPrice?.value || 0,
        discount: regularPrice?.value - discountPrice?.value || 0,
        ...(categoryItems &&
          Object.keys(categoryItems).length > 0 &&
          categoryItems),
      };

      const addToCartLayer = {
        event: "add_to_cart",
        ecommerce: {
          currency: "INR",
          value: discountPrice
            ? discountPrice.value * quantity
            : regularPrice.value * quantity,
          items: [item],
        },
      };

      if (productType.toLowerCase() === "configurableproduct") {
        if (!isMissingOptions) {
          item["item_variant"] = selectedVariantsArray;
        }
      }

      if (productType.toLowerCase() === "bundleproduct") {
        const bundleOptionsInProduct = product.items
          .find((item) => item.option_id === selectedBundle)
          .options.map((item) => item.uid);

        const bundleVariable = {
          cartId,
          product: {
            sku: product.sku,
            quantity,
            selected_options: bundleOptionsInProduct,
          },
        };
        // logGenerator(
        //   "info",
        //   `useProductFullDetail handleAddToCart bundleproduct addProductToCart cartId : ${cartId}`
        // );

        const result = await addProductToCart({
          variables:
            productDetails.enable_site_visit === 1 ||
            productDetails.enable_appliance_site_visit
              ? variables
              : bundleVariable,
        });

        if (!result?.data?.addProductsToCart?.user_errors?.length) {
          window.dataLayer.push(addToCartLayer);
        }
      } else {
        // console.log("addToCartLayer", addToCartLayer);
        // logGenerator(
        //   "info",
        //   `useProductFullDetail handleAddToCart not bundleproduct addProductToCart cartId : ${cartId}`
        // );

        const result = await addProductToCart({ variables });
        if (!result?.data?.addProductsToCart?.user_errors?.length) {
          window.dataLayer.push(addToCartLayer);
          // console.log("addToCartLayer", addToCartLayer);
        }
      }
    } catch {
      return;
    }
  };

  const handleSelectionChange = useCallback((optionId, selection) => {
    setOptionSelections((prevOptionSelections) => {
      const nextOptionSelections = new Map([...prevOptionSelections]);

      nextOptionSelections.set(optionId, selection);

      return nextOptionSelections;
    });
  }, []);

  useEffect(() => {
    if (product && product.__typename === "BundleProduct") {
      setSelectedBundle(product.items[0].option_id);
    }
  }, []);

  const productPrice = useMemo(
    () =>
      getConfigPrice(product, optionCodes, optionSelections, selectedBundle),
    [product, optionCodes, optionSelections, selectedBundle]
  );

  useEffect(() => {
    if (isSignedIn) {
      addrecentViewedProduct();
    }
  }, [isSignedIn]);

  useEffect(() => {
    const isExists = recentlyViewedData?.includes(product.sku);
    if (!isExists) {
      dispatch(setViewData({ id: product.id, sku: product.sku }));
    }
  }, [product]);

  function getCategoryItems() {
    let categoryCount = 1;
    const categoryItems = categoryNames.reduce((acc, item) => {
      if (item && item?.toLowerCase() !== "category" && item !== "product") {
        acc[`item_category${categoryCount}`] = item;
        categoryCount++;
      }
      return acc;
    }, {});

    return categoryItems;
  }

  useEffect(() => {
    const categoryItems = getCategoryItems();
    if (!alreadyViewed.includes(productDetails.sku)) {
      let item = {
        item_id: productDetails.sku,
        item_name: productDetails.name,
        item_brand: "Hettich",
        affiliation: "Hettich India Online",
        index: 0,
        quantity: quantity,
        price: discountPrice?.value || 0,
        discount: regularPrice?.value - discountPrice?.value || 0,
        ...(categoryItems &&
          Object.keys(categoryItems).length > 0 &&
          categoryItems),
      };

      if (product.__typename === "ConfigurableProduct") {
        if (!isMissingOptions) {
          item["item_variant"] = selectedVariantsArray;
        }
        // console.log("selectedVariantsArray", selectedVariantsArray);
      }

      const viewLayer = {
        event: "view_item",
        ecommerce: {
          currency: "INR",
          value: discountPrice?.value
            ? discountPrice?.value * quantity
            : regularPrice?.value * quantity,
          items: [item],
        },
      };

      if (window.dataLayer) {
        // console.log("viewLayer", viewLayer);
        if (product.__typename === "ConfigurableProduct") {
          if (!isMissingOptions) {
            // console.log("viewLayer on noMissingOptions", viewLayer);
            window.dataLayer.push(viewLayer);
          }
        } else {
          window.dataLayer.push(viewLayer);
        }
      }
      setAlreadyViewed([...alreadyViewed, productDetails.sku]);
    }
  }, [productDetails]);

  useEffect(() => {
    let modelTimer;
    if (product && product.id) {
      modelTimer = setTimeout(() => {
        get3dModel({
          variables: { id: product.id },
          context: { fetchOptions: { method: "GET" } },
        });
      }, 2000);
    }
    return () => clearTimeout(modelTimer);
  }, [product]);

  const cartCallToActionText = !isOutOfStock ? (
    <>
      <BsHandbag size={20} />
      Add to Cart
    </>
  ) : (
    <> Out of Stock</>
  );
  const isAddToCartDisabled = isOutOfStock || isAddProductLoading;

  const handlePinCodeModal = () => {
    const pincode = localStorage.getItem("PinCode");

    if (isMissingOptions) {
      addToast("Please Select All Variants", {
        appearance: "warning",
        autoDismiss: true,
        style: {
          backgroundColor: "#745600",
          color: "#fff",
        },
      });
    } else if (pincode) {
      checkPincode({
        variables: {
          pinCode: pincode,
          sku: productDetails.sku,
          state: Math.random(),
        },
      });
      return;
    } else {
      setOpenPinCodeModal(!openPinCodeModal);
    }
  };

  const cartActionContent = isSupportedProductType ? (
    <button
      disabled={isAddToCartDisabled}
      priority="high"
      type="submit"
      onClick={handlePinCodeModal}
    >
      {cartCallToActionText}
    </button>
  ) : (
    <div className={classes.unavailableContainer}>
      <Info />
      <p>
        <span>This product is currently unavailable for purchase.</span>
      </p>
    </div>
  );

  const buyNowActionContent = isSupportedProductType ? (
    !isOutOfStock ? (
      <button
        disabled={isAddToCartDisabled}
        onClick={(e) => {
          handlePinCodeModal();
          setBuyNow(true);
        }}
      >
        <IoCartOutline size={20} />
        {!isOutOfStock ? <>Buy it now</> : <> Out of Stock</>}
      </button>
    ) : null
  ) : (
    <div className={classes.unavailableContainer}>
      <Info />
      <p>
        <span>This product is currently unavailable for purchase.</span>
      </p>
    </div>
  );

  const pinCodeCheckFun = (avaiableFlag) => {
    if (avaiableFlag) {
      handleAddToCart(quantity);
      setOpenPinCodeModal(false);
    }
  };

  const isValidEmail = () => {
    if (NotifyEmail.trim() === "") {
      setNotifyEmailError("This is a Required Field! ");
      return false;
    } else if (!EMAIL_REGEX.test(NotifyEmail)) {
      setNotifyEmailError("Please enter Valid Email");
      return false;
    } else {
      setNotifyEmailError("");
      return true;
    }
  };
  const validate = () => {
    if (isValidEmail()) {
      return true;
    } else {
      isValidEmail();
    }
    return false;
  };

  const notSignedInNotify = (e) => {
    e.preventDefault();
    setButtonIsOpen(!buttonIsOpen);
  };

  const notSignedInNotifyModalSubmit = (e) => {
    e.preventDefault();

    if (!validate()) {
      return;
    }
    notifyProduct({
      variables: {
        product_id: product.id,
        email: NotifyEmail,
      },
    });
    setButtonIsOpen(!buttonIsOpen);
  };

  const signedInNotify = (e) => {
    e.preventDefault();
    notifyProduct({
      variables: {
        product_id: product.id,
        email: userData?.email,
      },
    });
  };

  const notifyMe = isOutOfStock ? (
    <button
      className={classes.notifyMe}
      onClick={isSignedIn ? signedInNotify : notSignedInNotify}
      disabled={isnotified}
    >
      {isnotified ? `Notified` : `Notify Me`}
    </button>
  ) : null;

  const handleClose = () => {
    setButtonIsOpen(false);
  };

  const modalContent = (
    <Modal show={buttonIsOpen} onHide={handleClose}>
      <Modal.Header closeButton>Notify Product</Modal.Header>
      <Modal.Body>
        <form onSubmit={notSignedInNotifyModalSubmit}>
          <div className="col-lg-12">
            <label htmlFor="NotifyEmail">
              Email<span style={{ color: "red" }}> *</span>
            </label>
            <input
              className="form-control"
              type="email"
              onBlur={isValidEmail}
              placeholder="Notify Email"
              onChange={(e) => setNotifyEmail(e.target.value)}
              value={NotifyEmail}
            />
            <div className="row">
              <div
                className={`text-left text-small col-md-12 ${classes.color_red}`}
              >
                {NotifyEmailError}
              </div>
            </div>
          </div>
          <div className={classes.ApplyButton}>
            <button
              disabled={notifyLoading}
              priority={"normal"}
              type={"submit"}
            >
              <span>Apply</span>
            </button>
          </div>
        </form>
      </Modal.Body>
    </Modal>
  );

  const errorMessage = {
    errorAddingProductToCart,
    notifyError,
  };

  const { discountPrice, regularPrice } = productPrice;

  const handleCloseReviewModal = () => setShowReviewModal(false);
  const handleShowReviewModal = () => setShowReviewModal(true);

  const bundleOptionsInProduct = product?.items
    ?.find((item) => item?.option_id === selectedBundle)
    ?.options?.map((item) => item?.uid);

  useEffect(() => {
    if (productDetails) {
      dispatch(saveShowEnquirePrdouctName(productDetails?.name));
    }
  }, [productDetails]);

  return {
    cartActionContent,
    optionSelections,
    availableOptions,
    handleSelectionChange,
    mediaGalleryEntries,
    productDetails,
    errorMessage,
    regularPrice,
    discountPrice,
    openPinCodeModal,
    pinCodeCheckFun,
    setProductZoom,
    zoom,
    notifyMe,
    modalContent,
    handlePinCodeModal,
    setQuantity,
    quantity,
    showReviewModal,
    handleCloseReviewModal,
    handleShowReviewModal,
    showThreeDModal,
    selectedBundle,
    setSelectedBundle,
    setShowThreeDModal,
    modelData,
    modelLoading,
    buyNowActionContent,
    configProductVariantId,
    isOutOfStock,
    selectedOptionsArray,
    bundleOptionsInProduct,
    isMissingOptions,
    vritualProductData,
    pincodeDataFromModal,
    setPincodeDataFromModal,
    isScrolledDown,
    categoryNames,
    setCategoryNames,
    influencerModalData,
    setInfluencerModalData,
    selectedVariantsArray,
  };
};
